import { createFileRoute } from "@tanstack/react-router";
import { Check } from "@/components/animate-ui/icons/check";
import { PublicLayout } from "@/components/public-layout";
import { buildLinks, buildMetaTags, getCanonical } from "@/lib/seo";

export const Route = createFileRoute("/terms")({
	head: () => {
		const canonical = getCanonical("/terms");
		const meta = buildMetaTags({
			title: "Terms of Service - m8tes",
			description: "Terms of Service and Commercial Terms for m8tes AI autonomous agent platform.",
			canonical,
			ogType: "website",
		});
		return {
			meta,
			links: buildLinks({ canonical }),
		};
	},
	component: TermsOfServicePage,
});

function TermsOfServicePage() {
	return (
		<PublicLayout>
			<div className="container mx-auto px-4 py-12 max-w-4xl">
				<div className="prose prose-neutral dark:prose-invert max-w-none">
					<h1 className="text-4xl font-bold mb-2">Terms of Service</h1>
					<p className="text-muted-foreground mb-8">Effective Date: November 7, 2025</p>

					{/* TLDR */}
					<section className="mb-12 p-6 bg-primary/10 border-l-4 border-primary rounded-r-lg">
						<h2 className="text-2xl font-semibold mb-4">TL;DR - What You Need to Know</h2>
						<ul className="space-y-3">
							<li className="flex items-start gap-3">
								<Check animateOnHover className="text-primary size-5 mt-0.5 shrink-0" />
								<div>
									<strong>Cancel Anytime</strong> - No lock-in, stop your subscription whenever you
									want
								</div>
							</li>
							<li className="flex items-start gap-3">
								<Check animateOnHover className="text-primary size-5 mt-0.5 shrink-0" />
								<div>
									<strong>You Own Your Content</strong> - Retain all rights to your data and AI
									outputs
								</div>
							</li>
							<li className="flex items-start gap-3">
								<Check animateOnHover className="text-primary size-5 mt-0.5 shrink-0" />
								<div>
									<strong>No Training on Your Data</strong> - Your content stays private and secure
								</div>
							</li>
							<li className="flex items-start gap-3">
								<Check animateOnHover className="text-primary size-5 mt-0.5 shrink-0" />
								<div>
									<strong>Transparent Pricing</strong> - Clear subscription terms, no hidden fees
								</div>
							</li>
							<li className="flex items-start gap-3">
								<Check animateOnHover className="text-primary size-5 mt-0.5 shrink-0" />
								<div>
									<strong>Fair Refund Policy</strong> - Standard cancellation terms apply
								</div>
							</li>
						</ul>
					</section>

					{/* Introduction */}
					<section className="mb-8">
						<p>
							These Terms of Service (the "<strong>Terms</strong>") govern your access to and use of
							the m8tes platform and services provided by m8tes, Inc. ("<strong>m8tes</strong>," "
							<strong>we</strong>," "<strong>us</strong>," or "<strong>our</strong>"). By accessing
							or using our Service, you agree to be bound by these Terms.
						</p>
						<p className="mt-4">
							<strong>PLEASE READ THESE TERMS CAREFULLY BEFORE USING OUR SERVICE.</strong> If you do
							not agree with these Terms, you may not access or use the Service.
						</p>
					</section>

					{/* 1. Service Terms & Account */}
					<section className="mb-8">
						<h2 className="text-3xl font-semibold mt-12 mb-4">1. Service Description & Account</h2>

						<h3 className="text-2xl font-semibold mt-8 mb-3">1.1 The Service</h3>
						<p>
							m8tes provides a platform for creating and managing autonomous AI agents (referred to
							as "<strong>Teammates</strong>" or "<strong>Mates</strong>") that can execute
							marketing tasks, integrate with third-party services, and assist with business
							operations (collectively, the "<strong>Service</strong>").
						</p>

						<h3 className="text-2xl font-semibold mt-8 mb-3">1.2 Account Requirements</h3>
						<p>To use the Service, you must:</p>
						<ul className="list-disc pl-6 space-y-2">
							<li>Be at least 18 years old</li>
							<li>Provide accurate and complete registration information</li>
							<li>
								Maintain the security of your account credentials and be responsible for all
								activities under your account
							</li>
							<li>Promptly notify us of any unauthorized use of your account</li>
							<li>Not share your account credentials or allow others to access your account</li>
						</ul>

						<h3 className="text-2xl font-semibold mt-8 mb-3">1.3 Account Registration</h3>
						<p>
							You may register using email and password or through third-party authentication
							providers (such as Google). You are responsible for maintaining accurate account
							information and updating it as necessary.
						</p>
					</section>

					{/* 2. Acceptable Use Policy */}
					<section className="mb-8">
						<h2 className="text-3xl font-semibold mt-12 mb-4">2. Acceptable Use Policy</h2>
						<p>
							You agree not to use the Service for any unlawful purpose or in any way that violates
							these Terms. Prohibited activities include, but are not limited to:
						</p>

						<h3 className="text-2xl font-semibold mt-8 mb-3">2.1 Prohibited Conduct</h3>
						<ul className="list-disc pl-6 space-y-2">
							<li>Violating any applicable laws, regulations, or third-party rights</li>
							<li>Engaging in fraudulent, deceptive, or misleading activities</li>
							<li>Distributing malware, viruses, or other harmful code</li>
							<li>
								Attempting to gain unauthorized access to the Service or other users' accounts
							</li>
							<li>
								Interfering with or disrupting the Service or servers/networks connected to the
								Service
							</li>
							<li>Using the Service to spam, harass, threaten, or abuse others</li>
							<li>Scraping or harvesting data from the Service without authorization</li>
						</ul>

						<h3 className="text-2xl font-semibold mt-8 mb-3">2.2 Content Restrictions</h3>
						<p>You may not use the Service to create, share, or process:</p>
						<ul className="list-disc pl-6 space-y-2">
							<li>
								Content that is illegal, harmful, defamatory, or infringes on intellectual property
								rights
							</li>
							<li>Content that promotes violence, hatred, or discrimination</li>
							<li>Content that exploits or endangers children or vulnerable populations</li>
							<li>Adult or sexually explicit content</li>
							<li>Misleading or false information intended to deceive</li>
						</ul>

						<h3 className="text-2xl font-semibold mt-8 mb-3">2.3 Competitive Use</h3>
						<p>
							You may not use the Service to develop, train, or improve competing AI models or
							products, or to reverse engineer our Service. You may not benchmark the Service
							against competing products without our prior written consent.
						</p>
					</section>

					{/* 3. Intellectual Property & Ownership */}
					<section className="mb-8">
						<h2 className="text-3xl font-semibold mt-12 mb-4">
							3. Intellectual Property & Ownership
						</h2>

						<h3 className="text-2xl font-semibold mt-8 mb-3">3.1 Your Content & Outputs</h3>
						<p>
							<strong>You retain all rights to your content</strong> that you submit to the Service,
							including task instructions, agent configurations, prompts, and any data you provide
							("
							<strong>Your Content</strong>"). You also own all outputs generated by the Service in
							response to Your Content ("
							<strong>Outputs</strong>"), subject to these Terms.
						</p>

						<h3 className="text-2xl font-semibold mt-8 mb-3">
							3.2 No Training on Customer Content
						</h3>
						<div className="bg-primary/10 border-l-4 border-primary p-6 my-4">
							<p className="font-semibold">
								m8tes does not train AI models on Your Content or Outputs from the Service.
							</p>
							<p className="mt-2 text-sm">
								Your tasks, conversations, agent configurations, and execution results are stored
								for service provision purposes only and are not used to train or improve AI models.
							</p>
						</div>

						<h3 className="text-2xl font-semibold mt-8 mb-3">3.3 m8tes Platform & IP</h3>
						<p>
							m8tes and its licensors retain all rights, title, and interest in and to the Service,
							including all software, infrastructure, algorithms, models, documentation, and
							technology used to provide the Service. These Terms do not grant you any ownership
							rights in the Service.
						</p>

						<h3 className="text-2xl font-semibold mt-8 mb-3">3.4 License Grants</h3>
						<p>
							<strong>License to m8tes:</strong> You grant m8tes a limited, non-exclusive, worldwide
							license to use, process, and display Your Content solely to provide the Service to
							you, including passing Your Content to AI service providers as necessary for task
							execution.
						</p>
						<p className="mt-4">
							<strong>License to You:</strong> Subject to these Terms, m8tes grants you a limited,
							non-exclusive, non-transferable, non-sublicensable license to access and use the
							Service for your internal business purposes.
						</p>

						<h3 className="text-2xl font-semibold mt-8 mb-3">3.5 Feedback</h3>
						<p>
							If you provide feedback, suggestions, or ideas about the Service, you grant m8tes a
							perpetual, irrevocable, worldwide, royalty-free license to use and incorporate such
							feedback into the Service without compensation or attribution.
						</p>
					</section>

					{/* 4. Payment Terms & Billing */}
					<section className="mb-8">
						<h2 className="text-3xl font-semibold mt-12 mb-4">4. Payment Terms & Billing</h2>

						<h3 className="text-2xl font-semibold mt-8 mb-3">4.1 Subscription Plans</h3>
						<p>
							m8tes offers various subscription plans with different features and usage limits,
							including Free, Pro, and Enterprise tiers. Current plans and pricing are available on
							our pricing page.
						</p>

						<h3 className="text-2xl font-semibold mt-8 mb-3">4.2 Payment Processing</h3>
						<p>
							Payments are processed by our third-party payment processor (Stripe). By providing
							payment information, you authorize us to charge your payment method for all fees
							incurred under your account. You are responsible for maintaining accurate and
							up-to-date payment information.
						</p>

						<h3 className="text-2xl font-semibold mt-8 mb-3">4.3 Subscription Billing</h3>
						<ul className="list-disc pl-6 space-y-2">
							<li>
								<strong>Billing Cycle:</strong> Subscriptions are billed monthly or annually based
								on your selected plan
							</li>
							<li>
								<strong>Auto-Renewal:</strong> Subscriptions automatically renew unless canceled
								before the renewal date
							</li>
							<li>
								<strong>Usage Charges:</strong> Some plans may include usage-based charges for
								exceeding included limits
							</li>
							<li>
								<strong>Taxes:</strong> Fees are exclusive of applicable taxes, which you are
								responsible for paying
							</li>
						</ul>

						<h3 className="text-2xl font-semibold mt-8 mb-3">4.4 Price Changes</h3>
						<p>
							We may change our pricing with 30 days' advance notice, except for changes required by
							law which may take effect immediately. Continued use of the Service after a price
							change constitutes acceptance of the new pricing.
						</p>

						<h3 className="text-2xl font-semibold mt-8 mb-3">4.5 Refunds & Cancellation</h3>
						<p>
							You may cancel your subscription at any time. Cancellation takes effect at the end of
							your current billing period. Fees are generally non-refundable except as required by
							law or at our sole discretion. We do not provide refunds or credits for partial months
							or years.
						</p>

						<h3 className="text-2xl font-semibold mt-8 mb-3">4.6 Payment Disputes</h3>
						<p>
							If you believe you have been incorrectly charged, you must contact us within 30 days
							of the charge. Failure to timely dispute a charge waives your right to dispute it.
						</p>
					</section>

					{/* 5. Third-Party Services */}
					<section className="mb-8">
						<h2 className="text-3xl font-semibold mt-12 mb-4">
							5. Third-Party Services & Integrations
						</h2>

						<h3 className="text-2xl font-semibold mt-8 mb-3">5.1 Third-Party Integrations</h3>
						<p>
							The Service allows you to connect third-party services (such as Google Ads) to your
							teammates. Your use of third-party services is subject to those services' terms and
							privacy policies. m8tes is not responsible for third-party services or their content.
						</p>

						<h3 className="text-2xl font-semibold mt-8 mb-3">5.2 Authorization & Access</h3>
						<p>
							By connecting third-party services, you authorize m8tes to access and use data from
							those services as necessary to provide the Service. You represent that you have all
							necessary rights and permissions to grant this authorization.
						</p>

						<h3 className="text-2xl font-semibold mt-8 mb-3">5.3 Third-Party Compliance</h3>
						<p>
							You are solely responsible for ensuring your use of third-party services complies with
							their terms of service, including any API usage restrictions, rate limits, or content
							policies. m8tes does not endorse or make representations about third-party services.
						</p>
					</section>

					{/* 6. Warranties & Disclaimers */}
					<section className="mb-8">
						<h2 className="text-3xl font-semibold mt-12 mb-4">6. Warranties & Disclaimers</h2>

						<h3 className="text-2xl font-semibold mt-8 mb-3">6.1 Service Warranty Disclaimer</h3>
						<div className="bg-muted p-6 rounded-lg border-2 border-border">
							<p className="font-semibold uppercase mb-2">
								THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTY OF ANY KIND.
							</p>
							<p className="text-sm">
								To the maximum extent permitted by law, m8tes disclaims all warranties, express or
								implied, including but not limited to implied warranties of merchantability, fitness
								for a particular purpose, title, non-infringement, and any warranties arising from
								course of dealing or usage of trade.
							</p>
						</div>

						<h3 className="text-2xl font-semibold mt-8 mb-3">6.2 No Guarantee of Availability</h3>
						<p>
							We do not warrant that the Service will be uninterrupted, error-free, secure, or
							available at all times. We may suspend or discontinue the Service or any features at
							any time without liability.
						</p>

						<h3 className="text-2xl font-semibold mt-8 mb-3">6.3 AI Output Accuracy</h3>
						<p>
							AI-generated outputs may contain errors, inaccuracies, or inappropriate content.{" "}
							<strong>
								You are solely responsible for reviewing, verifying, and validating all outputs
							</strong>{" "}
							before relying on or using them. m8tes makes no representations about the accuracy,
							reliability, completeness, or appropriateness of any outputs.
						</p>

						<h3 className="text-2xl font-semibold mt-8 mb-3">6.4 Your Warranties</h3>
						<p>You represent and warrant that:</p>
						<ul className="list-disc pl-6 space-y-2">
							<li>You have the right to use and provide all Your Content to the Service</li>
							<li>
								Your Content and use of the Service complies with all applicable laws and these
								Terms
							</li>
							<li>
								Your Content does not infringe any third-party intellectual property or other rights
							</li>
							<li>You have obtained all necessary consents and permissions for Your Content</li>
						</ul>
					</section>

					{/* 7. Limitation of Liability */}
					<section className="mb-8">
						<h2 className="text-3xl font-semibold mt-12 mb-4">7. Limitation of Liability</h2>

						<h3 className="text-2xl font-semibold mt-8 mb-3">7.1 Liability Cap</h3>
						<div className="bg-muted p-6 rounded-lg border-2 border-border">
							<p className="font-semibold uppercase mb-2">
								TO THE MAXIMUM EXTENT PERMITTED BY LAW, M8TES' TOTAL LIABILITY ARISING OUT OF OR
								RELATED TO THESE TERMS OR THE SERVICE SHALL NOT EXCEED THE TOTAL FEES YOU PAID TO
								M8TES IN THE 12 MONTHS PRECEDING THE EVENT GIVING RISE TO LIABILITY.
							</p>
						</div>

						<h3 className="text-2xl font-semibold mt-8 mb-3">7.2 Exclusion of Damages</h3>
						<div className="bg-muted p-6 rounded-lg border-2 border-border mt-4">
							<p className="font-semibold uppercase mb-2">
								M8TES SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR
								PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO:
							</p>
							<ul className="list-disc pl-6 space-y-1 text-sm mt-2">
								<li>Loss of profits, revenue, or business opportunities</li>
								<li>Loss of data or information</li>
								<li>Business interruption</li>
								<li>Loss of goodwill or reputation</li>
								<li>Cost of substitute services</li>
							</ul>
							<p className="text-sm mt-2">
								This exclusion applies regardless of the legal theory (contract, tort, negligence,
								strict liability, or otherwise) and even if m8tes has been advised of the
								possibility of such damages.
							</p>
						</div>

						<h3 className="text-2xl font-semibold mt-8 mb-3">7.3 Exceptions</h3>
						<p>
							The limitations in this section do not apply to: (a) your indemnification obligations;
							(b) your breach of Section 3 (Intellectual Property); (c) your violation of applicable
							law; or (d) liability that cannot be excluded or limited by law.
						</p>

						<h3 className="text-2xl font-semibold mt-8 mb-3">7.4 Essential Purpose</h3>
						<p>
							These limitations are fundamental elements of the agreement between you and m8tes. The
							Service would not be provided without these limitations.
						</p>
					</section>

					{/* 8. Indemnification */}
					<section className="mb-8">
						<h2 className="text-3xl font-semibold mt-12 mb-4">8. Indemnification</h2>

						<h3 className="text-2xl font-semibold mt-8 mb-3">8.1 Your Indemnification</h3>
						<p>
							You agree to indemnify, defend, and hold harmless m8tes, its affiliates, and their
							respective officers, directors, employees, and agents from and against any claims,
							liabilities, damages, losses, costs, or expenses (including reasonable attorneys'
							fees) arising out of or related to:
						</p>
						<ul className="list-disc pl-6 space-y-2">
							<li>Your Content or your use of Outputs</li>
							<li>Your use or misuse of the Service</li>
							<li>Your violation of these Terms</li>
							<li>Your violation of any applicable laws or regulations</li>
							<li>Your violation of any third-party rights</li>
							<li>Your third-party integrations or connections</li>
						</ul>

						<h3 className="text-2xl font-semibold mt-8 mb-3">8.2 m8tes Indemnification</h3>
						<p>
							m8tes will indemnify you against third-party claims that the Service, when used in
							accordance with these Terms, infringes a third party's intellectual property rights.
							This indemnification does not apply if the claim arises from: (a) Your Content; (b)
							modifications to the Service not made by m8tes; (c) your use of the Service in
							violation of these Terms; or (d) third-party services or content.
						</p>

						<h3 className="text-2xl font-semibold mt-8 mb-3">8.3 Procedures</h3>
						<p>
							The indemnified party must: (a) promptly notify the indemnifying party of the claim;
							(b) provide reasonable cooperation in the defense; and (c) allow the indemnifying
							party to control the defense and settlement, provided that no settlement may be made
							without the indemnified party's consent if it imposes obligations or admits liability
							on their behalf.
						</p>
					</section>

					{/* 9. Term & Termination */}
					<section className="mb-8">
						<h2 className="text-3xl font-semibold mt-12 mb-4">9. Term & Termination</h2>

						<h3 className="text-2xl font-semibold mt-8 mb-3">9.1 Term</h3>
						<p>
							These Terms begin when you first access the Service and continue until terminated in
							accordance with this section. Your subscription term is specified in your subscription
							plan (monthly or annual) and automatically renews unless canceled.
						</p>

						<h3 className="text-2xl font-semibold mt-8 mb-3">9.2 Termination for Convenience</h3>
						<p>
							You may terminate your account at any time through your account settings or by
							contacting us. Termination takes effect at the end of your current billing period
							unless you have violated these Terms, in which case termination may be immediate.
						</p>
						<p className="mt-4">
							m8tes may terminate or suspend your access to the Service at any time with 30 days'
							notice for any reason or no reason.
						</p>

						<h3 className="text-2xl font-semibold mt-8 mb-3">9.3 Termination for Breach</h3>
						<p>
							Either party may terminate these Terms immediately if the other party materially
							breaches these Terms and fails to cure the breach within 30 days of receiving written
							notice. m8tes may suspend or terminate your access immediately without notice if:
						</p>
						<ul className="list-disc pl-6 space-y-2">
							<li>You violate the Acceptable Use Policy (Section 2)</li>
							<li>Your account poses a security risk</li>
							<li>Required by law or governmental authority</li>
							<li>Your payment method fails or you have outstanding payment obligations</li>
						</ul>

						<h3 className="text-2xl font-semibold mt-8 mb-3">9.4 Effect of Termination</h3>
						<p>Upon termination:</p>
						<ul className="list-disc pl-6 space-y-2">
							<li>Your access to the Service will be immediately disabled</li>
							<li>
								You must cease all use of the Service and delete any copies of materials obtained
								from the Service
							</li>
							<li>
								m8tes may delete Your Content and account data in accordance with our data retention
								policies
							</li>
							<li>You remain liable for all fees incurred before termination</li>
							<li>No refunds will be provided for prepaid subscription fees</li>
						</ul>

						<h3 className="text-2xl font-semibold mt-8 mb-3">9.5 Survival</h3>
						<p>
							The following sections survive termination: 3 (Intellectual Property), 4.6 (Payment
							Disputes), 6 (Warranties & Disclaimers), 7 (Limitation of Liability), 8
							(Indemnification), 10 (Dispute Resolution), and 12 (General Provisions).
						</p>
					</section>

					{/* 10. Dispute Resolution & Arbitration */}
					<section className="mb-8">
						<h2 className="text-3xl font-semibold mt-12 mb-4">
							10. Dispute Resolution & Arbitration
						</h2>

						<h3 className="text-2xl font-semibold mt-8 mb-3">10.1 Informal Resolution</h3>
						<p>
							Before filing a legal claim, you agree to first contact us at support@m8tes.ai to
							attempt to resolve the dispute informally. Both parties agree to negotiate in good
							faith for at least 30 days before initiating arbitration or court proceedings.
						</p>

						<h3 className="text-2xl font-semibold mt-8 mb-3">10.2 Binding Arbitration</h3>
						<div className="bg-primary/10 border-l-4 border-primary p-6 my-4">
							<p className="font-semibold mb-2">MANDATORY ARBITRATION</p>
							<p className="text-sm">
								Any dispute, claim, or controversy arising out of or relating to these Terms or the
								Service that cannot be resolved informally shall be resolved by binding arbitration
								administered by the American Arbitration Association (AAA) under its Commercial
								Arbitration Rules.
							</p>
						</div>

						<h3 className="text-2xl font-semibold mt-8 mb-3">10.3 Arbitration Procedures</h3>
						<ul className="list-disc pl-6 space-y-2">
							<li>
								<strong>Venue:</strong> Arbitration shall take place in Delaware, unless otherwise
								agreed by the parties
							</li>
							<li>
								<strong>Rules:</strong> The arbitration will be conducted under AAA's Commercial
								Arbitration Rules
							</li>
							<li>
								<strong>Arbitrator:</strong> A single arbitrator will be appointed in accordance
								with AAA rules
							</li>
							<li>
								<strong>Decision:</strong> The arbitrator's decision is final and binding, and
								judgment may be entered in any court of competent jurisdiction
							</li>
							<li>
								<strong>Costs:</strong> Each party bears its own costs and fees, unless the
								arbitrator awards costs to the prevailing party
							</li>
						</ul>

						<h3 className="text-2xl font-semibold mt-8 mb-3">10.4 Class Action Waiver</h3>
						<div className="bg-muted p-6 rounded-lg border-2 border-border">
							<p className="font-semibold uppercase mb-2">
								YOU AND M8TES AGREE THAT EACH MAY BRING CLAIMS AGAINST THE OTHER ONLY IN YOUR OR ITS
								INDIVIDUAL CAPACITY, AND NOT AS A PLAINTIFF OR CLASS MEMBER IN ANY PURPORTED CLASS
								OR REPRESENTATIVE PROCEEDING.
							</p>
							<p className="text-sm mt-2">
								Unless both parties agree otherwise, the arbitrator may not consolidate multiple
								parties' claims and may not preside over any form of representative or class
								proceeding.
							</p>
						</div>

						<h3 className="text-2xl font-semibold mt-8 mb-3">10.5 Exceptions to Arbitration</h3>
						<p>
							Either party may seek equitable relief in court for infringement or misuse of
							intellectual property rights. Additionally, either party may bring a claim in small
							claims court if it qualifies.
						</p>

						<h3 className="text-2xl font-semibold mt-8 mb-3">10.6 Governing Law</h3>
						<p>
							These Terms shall be governed by and construed in accordance with the laws of the
							State of Delaware, without regard to its conflict of law provisions. Any arbitration
							or court proceeding shall apply Delaware law.
						</p>
					</section>

					{/* 11. Modifications */}
					<section className="mb-8">
						<h2 className="text-3xl font-semibold mt-12 mb-4">11. Modifications to Terms</h2>

						<h3 className="text-2xl font-semibold mt-8 mb-3">11.1 Right to Modify</h3>
						<p>
							m8tes reserves the right to modify these Terms at any time. We will provide notice of
							material changes by:
						</p>
						<ul className="list-disc pl-6 space-y-2">
							<li>Posting the updated Terms on this page</li>
							<li>Updating the "Effective Date" at the top</li>
							<li>Sending email notification to your account email (for material changes)</li>
						</ul>

						<h3 className="text-2xl font-semibold mt-8 mb-3">11.2 Acceptance of Changes</h3>
						<p>
							Material changes will take effect 30 days after notice is provided, except for changes
							required by law which may take effect immediately. Your continued use of the Service
							after the effective date of the updated Terms constitutes your acceptance of the
							changes.
						</p>

						<h3 className="text-2xl font-semibold mt-8 mb-3">11.3 Disagreement with Changes</h3>
						<p>
							If you do not agree to the modified Terms, you must stop using the Service and may
							terminate your account in accordance with Section 9 (Term & Termination).
						</p>
					</section>

					{/* 12. General Provisions */}
					<section className="mb-8">
						<h2 className="text-3xl font-semibold mt-12 mb-4">12. General Provisions</h2>

						<h3 className="text-2xl font-semibold mt-8 mb-3">12.1 Assignment</h3>
						<p>
							You may not assign or transfer these Terms or your account without m8tes' prior
							written consent. m8tes may assign these Terms or any rights hereunder without your
							consent, including to an affiliate or in connection with a merger, acquisition, or
							sale of assets.
						</p>

						<h3 className="text-2xl font-semibold mt-8 mb-3">12.2 Severability</h3>
						<p>
							If any provision of these Terms is found to be invalid or unenforceable, that
							provision will be limited or eliminated to the minimum extent necessary, and the
							remaining provisions will remain in full force and effect.
						</p>

						<h3 className="text-2xl font-semibold mt-8 mb-3">12.3 Force Majeure</h3>
						<p>
							Neither party shall be liable for any failure or delay in performance due to causes
							beyond its reasonable control, including acts of God, natural disasters, war,
							terrorism, riots, labor disputes, governmental actions, or internet/telecommunications
							failures.
						</p>

						<h3 className="text-2xl font-semibold mt-8 mb-3">12.4 Entire Agreement</h3>
						<p>
							These Terms, together with our Privacy Policy and any other agreements expressly
							incorporated by reference, constitute the entire agreement between you and m8tes
							regarding the Service and supersede all prior agreements and understandings.
						</p>

						<h3 className="text-2xl font-semibold mt-8 mb-3">12.5 No Waiver</h3>
						<p>
							m8tes' failure to enforce any right or provision of these Terms will not be deemed a
							waiver of such right or provision. Any waiver must be in writing and signed by m8tes.
						</p>

						<h3 className="text-2xl font-semibold mt-8 mb-3">12.6 Relationship</h3>
						<p>
							These Terms do not create any partnership, joint venture, employment, or agency
							relationship between you and m8tes. You may not make any representations or
							commitments on behalf of m8tes.
						</p>

						<h3 className="text-2xl font-semibold mt-8 mb-3">12.7 Contact Information</h3>
						<p>
							For questions about these Terms or to provide notice under these Terms, please contact
							us:
						</p>
						<div className="bg-muted p-6 rounded-lg mt-4">
							<p>
								<strong>m8tes, Inc.</strong>
							</p>
							<p>Email: support@m8tes.ai</p>
						</div>
					</section>

					{/* Acknowledgment */}
					<section className="mb-8 p-6 bg-muted rounded-lg">
						<p className="font-semibold mb-2">
							BY USING THE SERVICE, YOU ACKNOWLEDGE THAT YOU HAVE READ THESE TERMS, UNDERSTAND THEM,
							AND AGREE TO BE BOUND BY THEM.
						</p>
						<p className="text-sm">
							If you do not agree to these Terms, you must not access or use the Service.
						</p>
					</section>
				</div>
			</div>
		</PublicLayout>
	);
}
